// The resolution moment in the preprocess panel: always shown, editable, and —
// when the contract server found a genuine ambiguity — asked about before the
// forecast runs. Design: experimental/sooth-agents/docs/2026-09-21-ask-to-clarify-design.md.
//
// Pure helpers only; the panel component and the apply command build on these.

import { dayInZone, deadlineOf, exactDeadlineOf } from "./forecastClient.js";

/** The browser's own zone — the "your time" every option is shown in. */
export function askerZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/**
 * The event the deadline waits on when it has NO confirmed date, else "".
 *
 * Set by the server for "at Apple's next big event"-style questions: the date is
 * only rumored, so the panel says so and asks the user for one.
 */
export function unconfirmedEventOf(pp) {
  const v = pp?.custom_metadata?.resolution_unconfirmed;
  return typeof v === "string" ? v : "";
}

/**
 * The moments the asker must choose between, or [] when there is nothing to ask.
 *
 * For an ambiguity the server sends at least two DISTINCT readings; for an event
 * with no confirmed date one is enough (the user can also type a date). Anything
 * shorter or malformed is treated as no ask, so a bad payload can never hold a
 * forecast forever.
 */
export function resolutionOptionsOf(pp) {
  const raw = pp?.custom_metadata?.resolution_options;
  if (!Array.isArray(raw)) return [];
  const opts = raw.filter(
    (o) => o && typeof o.moment_utc === "string" && !isNaN(new Date(o.moment_utc).getTime()),
  );
  const min = unconfirmedEventOf(pp) ? 1 : 2;
  return opts.length >= min ? opts.slice(0, 3) : [];
}

/**
 * "Fri, Sep 25, 2026, 2:30 am EDT" — the instant in `zone`, or "" if unrenderable. The year
 * is on by default because many questions resolve in a later year.
 */
export function formatMomentInZone(iso, zone, { withZoneName = true, withYear = true } = {}) {
  const d = new Date(iso);
  if (!iso || isNaN(d.getTime())) return "";
  try {
    // en-US: its short month ("Sep") and zone abbreviation ("EDT") are the familiar
    // ones; en-GB renders "Sept" and "GMT-4".
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: zone || "UTC",
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      ...(withZoneName ? { timeZoneName: "short" } : {}),
    }).formatToParts(d);
    const get = (t) => parts.find((p) => p.type === t)?.value || "";
    const clock = `${get("hour")}:${get("minute")} ${get("dayPeriod").toLowerCase()}`;
    const tz = withZoneName ? ` ${get("timeZoneName")}` : "";
    const year = withYear ? `, ${get("year")}` : "";
    return `${get("weekday")}, ${get("month")} ${get("day")}${year}, ${clock}${tz}`;
  } catch {
    return d.toISOString().replace("T", " ").slice(0, 16) + " UTC";
  }
}

/** A zone's city for display: "Asia/Tokyo" -> "Tokyo". */
export function zoneCity(zone) {
  return (zone || "UTC").split("/").pop().replace(/_/g, " ");
}

const BASIS_REASON = {
  explicit: "as you set it",
  event: "scheduled event time",
  event_estimate: "estimated end of the event",
  subject_zone: "the subject's timezone",
  asker_zone: "your timezone",
  fallback: "UTC — timezone unknown",
};

/**
 * What the panel's RESOLVES line says, or null when the payload has no instant.
 *
 * `yours` is the moment in the asker's clock (always shown first); `theirs` is
 * the same moment in the zone it was decided in, only when that zone's clock
 * reads differently; `reason` is the label, else the rule that picked it.
 */
export function resolutionSummary(pp, zone = askerZone()) {
  const d = deadlineOf(pp);
  if (!d) return null;
  const iso = d.toISOString();
  const meta = pp?.custom_metadata || {};
  const decidedIn = meta.resolution_zone || "";
  const yours = formatMomentInZone(iso, zone);
  // The other zone's reading repeats the year only when it differs from the asker's
  // (a moment just past midnight on New Year's Eve), so the line stays short.
  const yearIn = (z) => formatMomentInZone(iso, z, { withZoneName: false }).split(", ")[2];
  const sameYear = !decidedIn || yearIn(decidedIn) === yearIn(zone);
  const theirsRaw = decidedIn
    ? formatMomentInZone(iso, decidedIn, { withZoneName: false, withYear: !sameYear })
    : "";
  const yoursBare = formatMomentInZone(iso, zone, { withZoneName: false, withYear: !sameYear });
  const theirs = theirsRaw && theirsRaw !== yoursBare ? `${theirsRaw} ${zoneCity(decidedIn)}` : "";
  const reason = meta.resolution_label || BASIS_REASON[meta.resolution_basis] || "";
  // An event's END estimated from its start and usual length — shown as such so the
  // user knows to adjust it if they know better.
  const estimated = meta.resolution_basis === "event_estimate";
  return { iso, yours, theirs, reason, estimated };
}

/** Naive-UTC spelling of an instant — the shape `scheduled_resolution_time` has on the wire. */
function naiveUtc(d) {
  return d.toISOString().slice(0, 19);
}

/**
 * The preprocess payload with its deadline replaced by `choice`.
 *
 * `choice` is `{moment_utc, zone, label}` — a server option, or a custom time the
 * user typed. The basis becomes `explicit`, the strongest rule, so nothing
 * downstream re-guesses it; `resolution_options` is dropped because the question
 * has been answered. Every reader of the deadline (`deadlineOf`, the forecast
 * builder, the proxy's contract seed) reads one of the fields set here.
 */
export function withResolutionMoment(pp, choice) {
  const d = new Date(choice?.moment_utc);
  if (!pp || isNaN(d.getTime())) return pp;
  const {
    resolution_options: _options,
    resolution_unconfirmed: _unconfirmed,
    ...meta
  } = pp.custom_metadata || {};
  const zone = choice.zone || askerZone();
  // The wording names the deadline's day too ("…by December 31, 2026?"): move it with the date.
  const fromDay = deadlineDayOf(pp);
  const toDay = dayInZone(d, zone);
  const reword = (text) =>
    typeof text === "string" && fromDay ? rewriteDayInText(text, fromDay, toDay) : text;
  return {
    ...pp,
    question_text: reword(pp.question_text),
    title: reword(pp.title),
    resolution_criteria: reword(pp.resolution_criteria),
    scheduled_resolution_time: naiveUtc(d),
    close_time: naiveUtc(d),
    custom_metadata: {
      ...meta,
      ...(typeof meta.assertion === "string" ? { assertion: reword(meta.assertion) } : {}),
      resolution_moment_utc: d.toISOString().replace(".000Z", "Z"),
      resolution_zone: zone,
      resolution_basis: "explicit",
      resolution_label: choice.label || "",
    },
  };
}

/** The deadline's calendar day ("YYYY-MM-DD") as the question means it, or "". */
function deadlineDayOf(pp) {
  const exact = exactDeadlineOf(pp);
  if (exact) return dayInZone(exact, pp.custom_metadata?.resolution_zone || "");
  // A date-only deadline is a naive midnight: read its day off the text, since Date() would
  // parse it in the browser's zone and move it a day in some.
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(pp?.scheduled_resolution_time || ""));
  return m ? m[1] : "";
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const MONTH_NAME =
  "(January|February|March|April|May|June|July|August|September|October|November|December|" +
  "Jan|Feb|Mar|Apr|Jun|Jul|Aug|Sept|Sep|Oct|Nov|Dec)";

const monthIndex = (name) => {
  const lower = name.toLowerCase();
  return MONTHS.findIndex((m) => m.toLowerCase().startsWith(lower.slice(0, 3)));
};
const ordinal = (n) => {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return "th";
  return { 1: "st", 2: "nd", 3: "rd" }[n % 10] || "th";
};
const pad2 = (n) => String(n).padStart(2, "0");

/**
 * `text` with each mention of the day `fromDay` ("YYYY-MM-DD") changed to `toDay`, in the
 * mention's own style: "December 31, 2026", "Dec. 31st", "31 December 2026", "2026-12-31",
 * "12/31/2026". A mention without a year gains one when the year changes. Other dates, and a day
 * named some other way ("end of the year"), are left as they are.
 */
export function rewriteDayInText(text, fromDay, toDay) {
  if (!text || fromDay === toDay) return text;
  const [fy, fm, fd] = fromDay.split("-").map(Number);
  const [ty, tm, td] = toDay.split("-").map(Number);
  if (!fy || !fm || !fd || !ty || !tm || !td) return text;
  const monthLike = (name) => {
    // Keep the mention's style: full name, or a three-letter abbreviation.
    const full = MONTHS[tm - 1];
    return MONTHS.some((m) => m.toLowerCase() === name.toLowerCase()) ? full : full.slice(0, 3);
  };
  const yearPart = (sep, year, added) => (year ? `${sep}${ty}` : ty !== fy ? `${added}${ty}` : "");
  const isFrom = (month, day, year) =>
    monthIndex(month) === fm - 1 && Number(day) === fd && (!year || Number(year) === fy);

  // "December 31, 2026" / "Dec. 31st" / "December 31"
  let out = text.replace(
    new RegExp(
      `\\b${MONTH_NAME}(\\.?)\\s+(\\d{1,2})(st|nd|rd|th)?\\b(?:(,?\\s+)(\\d{4})\\b)?`,
      "gi",
    ),
    (m, month, dot, day, suffix, sep, year) =>
      isFrom(month, day, year)
        ? `${monthLike(month)}${dot} ${td}${suffix ? ordinal(td) : ""}${yearPart(sep, year, ", ")}`
        : m,
  );
  // "31 December 2026" / "31st of Dec"
  out = out.replace(
    new RegExp(
      `\\b(\\d{1,2})(st|nd|rd|th)?\\s+(of\\s+)?${MONTH_NAME}(\\.?)(?:(,?\\s+)(\\d{4})\\b)?`,
      "gi",
    ),
    (m, day, suffix, of, month, dot, sep, year) =>
      isFrom(month, day, year)
        ? `${td}${suffix ? ordinal(td) : ""} ${of || ""}${monthLike(month)}${dot}${yearPart(sep, year, " ")}`
        : m,
  );
  // "2026-12-31"
  out = out.replace(new RegExp(`\\b${fy}-${pad2(fm)}-${pad2(fd)}\\b`, "g"), toDay);
  // "12/31/2026"
  out = out.replace(/\b(\d{1,2})\/(\d{1,2})\/(\d{4})\b/g, (m, mm, dd, yyyy) =>
    Number(mm) === fm && Number(dd) === fd && Number(yyyy) === fy
      ? `${mm.startsWith("0") ? pad2(tm) : tm}/${dd.startsWith("0") ? pad2(td) : td}/${ty}`
      : m,
  );
  return out;
}

/** `<input type="datetime-local">` value for an instant, in the browser's clock. */
export function toLocalInputValue(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** The instant a datetime-local value means in the browser's clock, or null. */
export function fromLocalInputValue(v) {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * What a number key does in the resolution picker, or null.
 *
 * Keys 1..N pick the numbered options; N+1 is the "your own date" card when it is
 * showing. Anything else — letters, 0, a number past the last card — does nothing.
 */
export function cardForKey(key, optionCount, showCustom) {
  const n = Number(key);
  if (!Number.isInteger(n) || n < 1) return null;
  if (n <= optionCount) return { type: "option", index: n - 1 };
  if (showCustom && n === optionCount + 1) return { type: "custom" };
  return null;
}
